import {Injectable} from '@angular/core';

/**
 * Warms the authenticated app chunk once a visitor shows intent to enter the app, so that submitting
 * the form does not then wait on a ~1.5 MB download.
 *
 * Triggered only from the pages that lead STRAIGHT into the app, on the first `input` or `pointerdown`
 * in one of their fields: sign-in and account activation, `/activate/…`, which logs the user in on
 * submit (`NewPasswordPageComponent.onFieldInteraction`, activate mode only). The other
 * public pages (sign-up, restore, resent-password, invite-mode new-password) deliberately never call
 * this: a visitor there is not about to enter the app, so warming would only cost them bytes.
 *
 * Why this is triggered by INTERACTION and not on idle after `load`:
 *
 * The mechanism available here is a dynamic `import()`, not `<link rel="prefetch">` — the chunk
 * filename is content-hashed and the esbuild-based Angular builder has no equivalent of webpack's
 * `webpackPrefetch` hint, so no static link tag can name the file. Unlike a real prefetch, `import()`
 * both downloads AND evaluates. That matters because Lighthouse's "unused JavaScript" and "total
 * byte weight" audits count every request in the trace, not just those before `load`: warming on a
 * timer would put the whole authenticated app back onto the login page's PageSpeed score, which is
 * the exact problem the lazy split exists to solve.
 *
 * Typing into or pressing on a form field is a strong signal of intent to log in, and it is something
 * a synthetic audit never does. So real users get a warm chunk by the time they finish typing their
 * password, while Lighthouse, and anyone who merely bounces off the page, pay nothing. Do NOT trigger
 * on focus: `appAutofocus` focuses fields programmatically on render, so a focus trigger fires with
 * no user action at all (it did, on activation).
 *
 * Further fences:
 *   - backs off on Save-Data / 2g-class connections, where this would cost the visitor real money;
 *   - defers the actual import to main-thread idle, so module evaluation cannot jank the very
 *     keystrokes that triggered it (which would show up as INP);
 *   - never rejects into the console — a failed warm-up must be invisible, because the on-demand
 *     load will simply happen later anyway.
 *
 * The `import()` path is the same module `main.routes.ts` lazy-loads, so esbuild emits one shared
 * chunk; a dynamic import adds no static edge, so nothing here pulls the app shell into the eager
 * bundle.
 */
@Injectable({providedIn: 'root'})
export class AppShellPrefetchService {
    private started = false;

    /** Overridable so specs can observe the warm-up without fetching the real chunk. */
    protected load: () => Promise<unknown> = () => import('../../app-shell/app-shell.module');

    warm(): void {
        if (this.started) {
            return;
        }
        this.started = true;

        const connection = (navigator as any).connection;
        if (connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || ''))) {
            return;
        }

        const run = () => {
            this.load().catch(() => { /* on-demand load will retry */ });
        };

        const idle = (window as any).requestIdleCallback;
        if (typeof idle === 'function') {
            idle(run, {timeout: 5000});
        } else {
            setTimeout(run, 500);
        }
    }
}
